/**
 * Touch detail — one row per touch.
 *
 * The rows are a separate artefact (`/api/touches`, see src/data/touchDetail.ts),
 * fetched when this page opens and kept columnar: a row here is an index into those
 * columns, never an object. Job title is the person's, looked up from the bundle's
 * people rather than repeated on every touch.
 *
 * Two bases (touchRows.ts; MEASURE-RULES Rule 5.6): the touches people made, by touch
 * date — the Ledger's, and the tab's default — or every touch on their resolved claims,
 * by the month each resolved (billed any month; by billed month in files built before
 * review #397) — what the person page, the team drawer and the Touch profile count,
 * and where their "Touches" buttons land (reviews #23, #47).
 *
 * The second is exact when the touch rows carry the claim link (files built from
 * 2026-10-02). On an older file it falls back to #86's reading: the touches on the
 * visits billed to the people picked, from Claim detail's rows — close, not exact.
 */
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppData } from '@/data/AppData'
import { useTouchDetail } from '@/data/touchDetail'
import { useClaimDetail } from '@/data/claimDetail'
import { billedBasis } from '@/data/billedBasis'
import { patientOf } from '@/data/patient'
import { readTouchDrill, type TouchDriversDrill, type TouchPickKey } from '@/data/touchDrill'
import { driverSource } from '@/data/touchDriverRows'
import { REPEAT, computeDrivers } from '@/data/touchDrivers'
import { TouchDetailShell } from '@/components/TouchDetailShell'
import { SearchPick } from '@/components/SearchPick'
import { dictOptions, indexOfValue, indexesOf } from '@/data/blankValue'
import { HiddenByStatus } from '@/components/HiddenByStatus'
import { applyFilters, useFilters } from '@/data/filters'
import { useLoadedDataset } from '@/data/DatasetProvider'
import type { Column } from '@/components/Table'
import { linked } from '@/components/FacilityLink'
import type { ExportColumn } from '@/ui/export'
import { int, monthSpan } from '@/format'
import { useUrlState, text, json, bool, list, type Codec } from '@/ui/urlState'
import { pickAdapter } from '@/state/dictShare'
import { DIMENSION_HELP, type Definition } from '@/measures/definitions'
import { useTouchPeople, touchColumnPicks } from '@/data/touchScope'
import { claimKey, claimMonth, hasClaimBasis, selectTouches, touchesByClaim, type TouchBasis } from '@/data/touchRows'

/**
 * Her seven dropdowns, in her order, and payer type — a column the rows carry, so an
 * Overview payer-type bar's touches are its own rather than everyone's who works it
 * (review #177). Each holds a dictionary index, '' for All.
 */
const PICKERS = [
  { key: 'e', label: 'Employee' },
  { key: 'fg', label: 'Facility group' },
  { key: 'f', label: 'Facility' },
  { key: 's', label: 'State' },
  { key: 'p', label: 'Payer type', help: DIMENSION_HELP.payerType },
  { key: 'sd', label: 'Status' },
  { key: 'ad', label: 'Action' },
  { key: 'wf', label: 'Worked from' },
] as const satisfies readonly { key: string; label: string; help?: Definition }[]
type PickerKey = (typeof PICKERS)[number]['key']
const NONE: Record<PickerKey, string> = { e: '', fg: '', f: '', s: '', p: '', sd: '', ad: '', wf: '' }

/** Touch date, the default, is left out of the URL. */
const basisCodec: Codec<TouchBasis> = {
  parse: (s) => (s === 'claim' ? 'claim' : 'touch'),
  format: (v) => (v === 'claim' ? 'claim' : null),
}

const BASES: { value: TouchBasis; label: string; title: string }[] = [
  {
    value: 'claim',
    label: 'On their claims, by month resolved',
    title:
      'Every touch on their claims resolved in the months picked, whenever billed, whoever made it and whenever — what the Touch profile and the person page count',
  },
  {
    value: 'touch',
    label: 'Made by them, by touch date',
    title: 'The touches they made, dated in the months picked — the Ledger’s Touch detail',
  },
]

/** A dropdown's pick as the indexes it matches: every blank spelling, for an "Unknown". */
const wantOf = (dict: readonly string[], picked: string): ReadonlySet<number> => {
  const i = Number(picked)
  return new Set(dict[i] !== undefined ? indexesOf(dict, dict[i]!) : [i])
}

/** "2026-03-01" -> "3/1/2026", as the Ledger shows it. */
const usDate = (v: string) =>
  /^\d{4}-\d{2}-\d{2}/.test(v) ? `${+v.slice(5, 7)}/${+v.slice(8, 10)}/${v.slice(0, 4)}` : v

export function TouchDetail() {
  const { scope, derived } = useAppData()
  const { filters } = useFilters()
  const dataset = useLoadedDataset()
  const { data, error } = useTouchDetail()
  /*
   * Kept for Back in this history entry, not the URL: the Employee dropdown, the search
   * and the team name people (ui/urlState.ts).
   */
  /** Names nobody, so it is in the URL. */
  const [basisWanted, setBasisWanted] = useUrlState('basis', 'touch' as TouchBasis, basisCodec)
  // A shared link carries the dropdowns as values, not this caller's indexes
  // (state/dictShare.ts): the Employee one by owner on the claim basis, as `dictOf`.
  const pickDicts = useMemo(() => {
    if (!data) return null
    const owners = hasClaimBasis(data) && basisWanted === 'claim'
    return Object.fromEntries(
      PICKERS.map((k) => [k.key, ((k.key === 'e' && owners ? data.dict.o : data.dict[k.key]) ?? []) as readonly string[]]),
    )
  }, [data, basisWanted])
  const [picked, setPicked] = useUrlState('pick', NONE, json(NONE), {
    personal: true,
    share: pickAdapter(pickDicts),
  })
  // Free text can hold anything (a patient's name): never put in a shared link.
  const [q, setQ] = useUrlState('q', '', text, { personal: true, share: false })
  // What is in the box as it is typed; `q`, the search applied, follows it after a pause.
  const [typed, setTyped] = useState(q)
  /** A team handed over by another tab's "Touches" button; see touchDrill.ts. */
  const [teamRaw, setTeamRaw] = useUrlState(
    'team',
    null as { names: string[]; label: string } | null,
    json<{ names: string[]; label: string } | null>(null),
    { personal: true },
  )
  const team = useMemo(
    () => (teamRaw ? { names: new Set(teamRaw.names), label: teamRaw.label } : null),
    [teamRaw],
  )
  const setTeam = (t: { names: Set<string>; label: string } | null) =>
    setTeamRaw(t ? { names: [...t.names], label: t.label } : null)
  /**
   * One claim's touches, handed over by Claim detail's Touches column (review #165):
   * its visit and payer, every date, by anyone. The visit is a patient's, so it is
   * kept out of the URL with the people.
   */
  const [oneClaim, setOneClaim] = useUrlState(
    'claim',
    null as { visit: string; payer: string } | null,
    json<{ visit: string; payer: string } | null>(null),
    // A patient's visit: never in a link, not even as a key.
    { personal: true, share: false },
  )
  /**
   * Exactly the rows a Touch drivers count counted (touchDrill.ts). Names people and
   * visits: kept in the history entry, never in a link.
   */
  const [drivers, setDrivers] = useUrlState(
    'drivers',
    null as TouchDriversDrill | null,
    json<TouchDriversDrill | null>(null),
    { personal: true, share: false },
  )
  /** Whether this entry's drill has been applied; see below. */
  const [drilled, setDrilled] = useUrlState('drilled', false, bool, { personal: true })
  /**
   * Months a drill narrowed to inside the top bar's range — the Touch profile's held
   * month. Names nobody, so it is in the URL.
   */
  const [only, setOnly] = useUrlState('only', [] as string[], list)
  const months = useMemo(
    () => (only.length ? scope.months.filter((m) => only.includes(m)) : scope.months),
    [scope.months, only],
  )
  /** The rows carry their claim, so "on their claims" is exact (touchRows.ts). */
  const claimLink = !!data && hasClaimBasis(data)
  /** The Employee dropdown names claim owners: the exact claim basis only. */
  const ownerPick = claimLink && basisWanted === 'claim'
  /** The Employee dropdown's dictionary: the toucher, or on the claim basis the owner. */
  const dictOf = (key: PickerKey): string[] =>
    (key === 'e' && ownerPick ? data?.dict.o : data?.dict[key]) ?? []
  /** Whose touches: the Employee picked, or the team a button handed over. */
  const owners = useMemo(() => {
    if (picked.e !== '') {
      const name = dictOf('e')[Number(picked.e)]
      return name ? new Set([name]) : null
    }
    return team ? team.names : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, picked.e, team, ownerPick])
  const ownerLabel =
    owners === null ? '' : owners.size === 1 ? [...owners][0]! : (team?.label ?? `${owners.size} people`)
  /** Without the claim link, "on their claims" needs someone picked to join on. */
  const basis: TouchBasis =
    basisWanted === 'claim' && (claimLink || owners !== null) ? 'claim' : 'touch'

  // Arriving from a "Touches" button: start from clean filters, then narrow to what it
  // carried — one person in the Employee dropdown, several as a team tag. Once per
  // history entry, and only once the rows (and so the dropdown's values) are here.
  // Recorded IN the entry, so coming Back to it keeps whatever was picked since.
  const location = useLocation()
  useEffect(() => {
    const drill = readTouchDrill(location.state)
    if (!drill || !data || drilled) return
    setDrilled(true)
    setTyped('')
    setQ('')
    setOnly(drill.months ?? [])
    setOneClaim(drill.claim ?? null)
    setDrivers(drill.drivers ?? null)
    if (drill.drivers) {
      // The rows the count counted: the dropdowns start clear.
      setPicked(NONE)
      setTeam(null)
      setBasisWanted('touch')
      return
    }
    if (drill.claim) {
      // Exactly the rows the column counted: nothing else narrows them.
      setPicked(NONE)
      setTeam(null)
      setBasisWanted('touch')
      return
    }
    // An Overview slice the rows carry goes in its own dropdown, as hers does.
    // Blank-for-blank, so an "Unknown" bar finds every blank spelling (review #177).
    const pick = Object.entries(drill.pick ?? {}).map(([k, v]) => {
      const dict = data.dict[k as TouchPickKey]
      return [k, dict ? indexOfValue(dict, v ?? '') : -1] as const
    })
    if (pick.length && pick.every(([, i]) => i >= 0)) {
      setPicked({ ...NONE, ...Object.fromEntries(pick.map(([k, i]) => [k, String(i)])) })
      setTeam(null)
      setBasisWanted('touch')
      return
    }
    const byClaim = drill.basis === 'claim'
    setBasisWanted(byClaim ? 'claim' : 'touch')
    const names = byClaim && hasClaimBasis(data) ? data.dict.o! : data.dict.e
    const one = drill.employees.length === 1 ? names.indexOf(drill.employees[0]!) : -1
    setPicked(one >= 0 ? { ...NONE, e: String(one) } : NONE)
    setTeam(one >= 0 ? null : { names: new Set(drill.employees), label: drill.label })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, data, drilled])

  // Search waits for a pause in typing, as hers does; each pass scans every touch.
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim().toLowerCase()), 250)
    return () => clearTimeout(t)
  }, [typed])

  // Whose touches, with the Facility group filter on the rows themselves (touchScope.ts).
  const people = useTouchPeople()
  // The same people with Employee status set to both: a touch whose owner is here but
  // not in scope is hidden by that control alone, and is counted so the page says so.
  const anyStatus = useMemo(
    () =>
      filters.employmentStatus === 'both'
        ? people
        : applyFilters(derived, dataset.composition, {
            ...filters,
            employmentStatus: 'both',
            facilityGroup: [],
          }).people,
    [filters, people, derived, dataset.composition],
  )

  // The fallback's claim rows, fetched only when it is in use.
  const claimRows = useClaimDetail(!claimLink && owners !== null)
  /** Fallback: visits billed to the people picked in the months, as #86 read them. */
  const visits = useMemo(() => {
    if (claimLink || !owners || !claimRows.data) return null
    const { dict, cols, n } = claimRows.data
    const inMonths = new Set(months)
    const okDate = dict.d.map((d) => inMonths.has(d.slice(0, 7)))
    const out = new Set<string>()
    for (let i = 0; i < n; i++)
      if (okDate[cols.d[i]!] && billedBasis(cols, i) && owners.has(dict.e[cols.e[i]!]!)) out.add(cols.v[i]!)
    return out
  }, [claimLink, owners, claimRows.data, months])

  /** The top bar's Overview slice and Facility group, on the rows' own columns. */
  const columnPicks = useMemo(() => touchColumnPicks(data, filters), [data, filters])

  /** Both readings, so each can show its count. */
  const { made, onClaims } = useMemo(() => {
    if (!data) return { made: null, onClaims: null }
    const { dict, cols } = data
    // Visit, facility, payer, employee, note, status, action and patient — the
    // columns her search reads.
    const hit = (i: number) =>
      (!!cols.pt && !!dict.pt && dict.pt[cols.pt[i]!]!.toLowerCase().includes(q)) ||
      cols.v[i]!.toLowerCase().includes(q) ||
      dict.f[cols.f[i]!]!.toLowerCase().includes(q) ||
      dict.pn[cols.pn[i]!]!.toLowerCase().includes(q) ||
      dict.e[cols.e[i]!]!.toLowerCase().includes(q) ||
      cols.note[i]!.toLowerCase().includes(q) ||
      dict.sd[cols.sd[i]!]!.toLowerCase().includes(q) ||
      dict.ad[cols.ad[i]!]!.toLowerCase().includes(q)
    // The Employee dropdown is `owners`; every other dropdown narrows both readings.
    const picks = [
      ...PICKERS.filter((k) => k.key !== 'e' && picked[k.key] !== '').map((k) => ({
        col: cols[k.key],
        want: wantOf(dict[k.key], picked[k.key]),
      })),
      ...columnPicks,
    ]
    const sel = {
      // The top bar's month range, as hers applies it.
      months: new Set(months),
      inScope: new Set(people.map((p) => p.employee)),
      inAnyStatus: new Set(anyStatus.map((p) => p.employee)),
      team: owners,
      picks,
      hit: q ? hit : null,
    }
    const made = selectTouches(data, { ...sel, basis: 'touch' })
    if (claimLink) return { made, onClaims: selectTouches(data, { ...sel, basis: 'claim' }) }
    if (!visits) return { made, onClaims: null }
    // Fallback: rows are already scoped server-side to what the caller may see.
    const rows: number[] = []
    for (let i = 0; i < data.n; i++)
      if (visits.has(cols.v[i]!) && !picks.some((p) => !p.want.has(p.col[i]!)) && (!q || hit(i)))
        rows.push(i)
    return { made, onClaims: { rows, hidden: 0, claims: null } }
  }, [data, claimLink, visits, people, anyStatus, months, owners, picked, q, columnPicks])

  /** One claim's rows (review #165), narrowed only by the dropdowns and search. */
  const wantClaim = oneClaim !== null
  const claimIndex = useMemo(() => (data && wantClaim ? touchesByClaim(data) : null), [data, wantClaim])
  const claimTouches = useMemo(() => {
    if (!data || !oneClaim || !claimIndex) return null
    const { dict, cols } = data
    const on = claimIndex.get(claimKey(oneClaim.visit, oneClaim.payer)) ?? []
    const picks = PICKERS.filter((k) => picked[k.key] !== '').map((k) => ({
      col: cols[k.key],
      want: wantOf(dict[k.key], picked[k.key]),
    }))
    const hit = (i: number) =>
      (!!cols.pt && !!dict.pt && dict.pt[cols.pt[i]!]!.toLowerCase().includes(q)) ||
      cols.v[i]!.toLowerCase().includes(q) ||
      dict.f[cols.f[i]!]!.toLowerCase().includes(q) ||
      dict.e[cols.e[i]!]!.toLowerCase().includes(q) ||
      cols.note[i]!.toLowerCase().includes(q) ||
      dict.sd[cols.sd[i]!]!.toLowerCase().includes(q) ||
      dict.ad[cols.ad[i]!]!.toLowerCase().includes(q)
    const rows = on.filter((i) => !picks.some((p) => !p.want.has(p.col[i]!)) && (!q || hit(i)))
    return { rows, hidden: 0, claims: null }
  }, [data, oneClaim, claimIndex, picked, q])

  /**
   * Touch drivers' rows (touchDriverRows.ts): the drill's people, the top bar's months,
   * the trimmed status and action, the claims, and with `repeatsOnly` the repeat flag on
   * the same look-back. Then the dropdowns and search, as everywhere here.
   */
  const driverTouches = useMemo(() => {
    if (!data || !drivers) return null
    const src = driverSource(data, {
      months,
      lookBack: drivers.repeatsOnly ? drivers.lookBack : [],
      people: new Set(drivers.people),
      picks: columnPicks,
    })
    const flags = drivers.repeatsOnly ? computeDrivers(src.rows).flags : null
    const r = src.rows
    const s = drivers.status === undefined ? null : src.statuses.indexOf(drivers.status.trim())
    const a = drivers.action === undefined ? null : src.actions.indexOf(drivers.action.trim())
    const claimSet = drivers.claims ? new Set(drivers.claims.map((c) => `${c.f}${c.v}`)) : null
    const { dict, cols } = data
    const picks = PICKERS.filter((k) => picked[k.key] !== '').map((k) => ({
      col: cols[k.key],
      want: wantOf(dict[k.key], picked[k.key]),
    }))
    const hit = (i: number) =>
      cols.v[i]!.toLowerCase().includes(q) ||
      dict.f[cols.f[i]!]!.toLowerCase().includes(q) ||
      dict.pn[cols.pn[i]!]!.toLowerCase().includes(q) ||
      cols.note[i]!.toLowerCase().includes(q) ||
      dict.sd[cols.sd[i]!]!.toLowerCase().includes(q) ||
      dict.ad[cols.ad[i]!]!.toLowerCase().includes(q)
    const rows: number[] = []
    for (let k = 0; k < r.n; k++) {
      if (!r.counted[k]) continue
      if (s !== null && r.status[k] !== s) continue
      if (a !== null && r.action[k] !== a) continue
      if (flags && !(flags[k]! & REPEAT)) continue
      const i = src.fileRow[k]!
      if (claimSet && !claimSet.has(`${dict.f[cols.f[i]!]}${cols.v[i]}`)) continue
      if (picks.some((p) => !p.want.has(p.col[i]!))) continue
      if (q && !hit(i)) continue
      rows.push(i)
    }
    // The file's own order, as every other reading here.
    rows.sort((x, y) => x - y)
    return { rows, hidden: 0, claims: null }
  }, [data, drivers, months, columnPicks, picked, q])

  const shown = driverTouches ?? claimTouches ?? (basis === 'claim' ? onClaims : made)
  const rows = shown?.rows ?? []
  const hidden = shown?.hidden ?? 0

  const columns = useMemo<Column<number>[]>(() => {
    if (!data) return []
    const { dict, cols } = data
    const txt = (key: Exclude<keyof typeof dict, 'o' | 'rm' | 'bm' | 'pt' | 'wr'>) => (i: number) => dict[key][cols[key][i]!] ?? ''
    const person = (i: number) => derived.byName.get(dict.e[cols.e[i]!]!)
    const patient = patientOf(data)
    const mono = (s: string) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{s}</span>
    const col = (
      key: string,
      header: string,
      get: (i: number) => string,
      width?: number,
      help?: Definition,
    ): Column<number> => ({ key, header, width, sort: get, render: get, help })
    return [
      col('e', 'Employee', txt('e'), 170),
      col('jt', 'Job title', (i) => person(i)?.jobTitle ?? ''),
      // Both open on Facilities (review #226).
      linked(col('fg', 'Facility group', txt('fg')), 'group'),
      linked(col('f', 'Facility', txt('f'), 220), 'facility'),
      col('s', 'State', txt('s')),
      // Where Shayna pointed (review #70). Only once the build carries names.
      ...(patient ? [col('pt', 'Patient', patient, 180)] : []),
      { key: 'v', header: 'Visit', sort: (i) => cols.v[i]!, render: (i) => mono(cols.v[i]!) },
      { key: 'd', header: 'Date', sort: txt('d'), render: (i) => mono(usDate(txt('d')(i))) },
      col('wf', 'Worked from', txt('wf')),
      col('wl', 'Worklist', txt('wl'), 220),
      col('p', 'Payer type', txt('p'), undefined, DIMENSION_HELP.payerType),
      col('pn', 'Payer name', txt('pn'), undefined, DIMENSION_HELP.payerName),
      // The weight of the claim this touch is on, and why (docs/RULES.md). The claim is
      // found by visit + payer; a touch whose claim Claim detail does not carry gets
      // the hard-payer rule only, and says so.
      ...(cols.w
        ? [
            {
              key: 'w',
              header: 'Weight',
              align: 'right' as const,
              sort: (i: number) => cols.w![i] ?? 1,
              render: (i: number) => {
                const why = (cols.wr && dict.wr?.[cols.wr[i]!]) || ''
                return (
                  <span className="mono" title={why || 'No rule applies: counts 1'} style={{ fontSize: 'var(--t-table)', ...(cols.w![i] === 1 ? { color: 'var(--ink-3)' } : {}) }}>
                    ×{cols.w![i]}
                  </span>
                )
              },
            },
            col('wr', 'Weighted because', (i) => (cols.wr && dict.wr?.[cols.wr[i]!]) || '', 220),
          ]
        : []),
      col('sd', 'Status', txt('sd'), 220),
      col('ad', 'Action', txt('ad'), 220),
      {
        key: 'note',
        header: 'Note',
        width: 360,
        sort: (i) => cols.note[i]!,
        render: (i) => (
          <span
            title={cols.note[i] || undefined}
            style={{
              display: 'block',
              whiteSpace: 'normal',
              maxWidth: 360,
              fontSize: 11.5,
              lineHeight: 1.3,
              color: 'var(--ink-2)',
            }}
          >
            {cols.note[i]}
          </span>
        ),
      },
    ]
  }, [data, derived])

  const exportColumns = useMemo<ExportColumn<number>[]>(() => {
    if (!data) return []
    const { dict, cols } = data
    const txt = (key: Exclude<keyof typeof dict, 'o' | 'rm' | 'bm' | 'pt' | 'wr'>) => (i: number) => dict[key][cols[key][i]!] ?? ''
    const person = (i: number) => derived.byName.get(dict.e[cols.e[i]!]!)
    const patient = patientOf(data)
    return [
      { header: 'Employee', value: txt('e') },
      { header: 'Job title', value: (i) => person(i)?.jobTitle ?? '' },
      { header: 'Facility group', value: txt('fg') },
      { header: 'Facility', value: txt('f') },
      { header: 'State', value: txt('s') },
      ...(patient ? [{ header: 'Patient', value: patient }] : []),
      { header: 'Visit', value: (i) => cols.v[i] ?? '' },
      { header: 'Date', value: (i) => usDate(txt('d')(i)) },
      { header: 'Worked from', value: txt('wf') },
      { header: 'Worklist', value: txt('wl') },
      { header: 'Payer type', value: txt('p') },
      { header: 'Payer name', value: txt('pn') },
      ...(cols.w
        ? [
            { header: 'Weight', value: (i: number) => cols.w![i] ?? 1 },
            { header: 'Weighted because', value: (i: number) => (cols.wr && dict.wr?.[cols.wr[i]!]) || '' },
          ]
        : []),
      { header: 'Status', value: txt('sd') },
      { header: 'Action', value: txt('ad') },
      { header: 'Note', value: (i) => cols.note[i] ?? '' },
    ]
  }, [data, derived])

  return (
    <TouchDetailShell
      title="Touch detail"
      grain="one row per touch"
      filters={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end' }}>
          {!oneClaim && !drivers && (claimLink || owners !== null) && (
            <div style={{ display: 'grid', gap: 4 }}>
              <span className="eyebrow">Touches</span>
              <div role="radiogroup" aria-label="Which touches" style={seg}>
                {BASES.map((b) => {
                  const on = b.value === basis
                  return (
                    <button
                      key={b.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      title={b.title}
                      onClick={() => {
                        if (on || !data) return
                        setBasisWanted(b.value)
                        // On the exact basis the Employee dropdown changes dictionary; keep
                        // the same person, as a tag if the other side has none of theirs.
                        if (picked.e === '' || !claimLink) return
                        const name = dictOf('e')[Number(picked.e)] ?? ''
                        const i = (b.value === 'claim' ? data.dict.o! : data.dict.e).indexOf(name)
                        setPicked((p) => ({ ...p, e: i >= 0 ? String(i) : '' }))
                        if (i < 0) setTeam({ names: new Set([name]), label: name })
                      }}
                      style={{
                        ...segBtn,
                        background: on ? 'var(--accent)' : 'transparent',
                        color: on ? 'var(--on-accent, #fff)' : 'var(--ink-2)',
                      }}
                    >
                      {owners === null
                        ? // A file built before review #397 links claims by bill month.
                          b.value === 'claim' && data && claimMonth(data)?.key === 'bm'
                          ? 'On their claims, by billed month'
                          : b.label
                        : b.value === 'claim'
                          ? `Touches on ${ownerLabel}’s claims · ${
                              onClaims ? int(onClaims.rows.length) : claimRows.error ? '—' : '…'
                            }`
                          : `Touches ${ownerLabel} made · ${made ? int(made.rows.length) : '…'}`}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {/* Every dropdown searchable, as Claim detail's (review #233). */}
          {PICKERS.map((pk) => {
            const { key, label } = pk
            const dict = dictOf(key)
            const v = picked[key]
            const shown = v === '' || !dict[Number(v)] ? v : String(indexOfValue(dict, dict[Number(v)]!))
            return (
              <SearchPick
                key={key}
                label={key === 'e' && ownerPick ? 'Claim owner' : label}
                help={'help' in pk ? pk.help : undefined}
                value={shown}
                allValue=""
                allLabel="All"
                options={dictOptions(dict)}
                onChange={(next) => setPicked((p) => ({ ...p, [key]: next }))}
                minWidth={key === 'e' || key === 'f' || key === 'fg' ? 200 : 130}
              />
            )
          })}
          <label style={{ display: 'grid', gap: 4 }}>
            <span className="eyebrow">Search</span>
            <input
              type="search"
              value={typed}
              placeholder={data?.dict.pt ? "visit, patient, facility, payer, note" : "visit, facility, payer, note"}
              onChange={(e) => setTyped(e.target.value)}
              style={{
                fontFamily: 'var(--sans)',
                fontSize: 'var(--t-table)',
                padding: '5px 7px',
                borderRadius: 6,
                border: '1px solid var(--rule-2)',
                background: 'var(--surface)',
                color: 'var(--ink)',
              }}
            />
          </label>
          <button
            type="button"
            className="reset-btn"
            style={{ marginLeft: 'auto' }}
            onClick={() => {
              setPicked(NONE)
              setTeam(null)
              setOnly([])
              setOneClaim(null)
              setDrivers(null)
              setTyped('')
              setQ('')
            }}
          >
            Clear
          </button>
        </div>
      }
      summary={
        data && (
          <>
            {drivers ? (
              <>
                {int(rows.length)} touches in {monthSpan(months)} from Touch drivers{' '}
                <span className="pill-sel" data-testid="drivers-drill">
                  {drivers.label}
                  {drivers.repeatsOnly ? ' · repeats only' : ''}
                  <button type="button" title="Clear" onClick={() => setDrivers(null)}>
                    ×
                  </button>
                </span>
              </>
            ) : oneClaim ? (
              <>
                {int(rows.length)} touches on this claim, any date, by anyone{' '}
                <span className="pill-sel" data-testid="one-claim">
                  Visit {oneClaim.visit} · {oneClaim.payer || 'no payer'}
                  <button type="button" title="Clear" onClick={() => setOneClaim(null)}>
                    ×
                  </button>
                </span>
              </>
            ) : basis === 'claim' && claimLink ? (
              <>
                {int(rows.length)} touches on {int(shown?.claims ?? 0)}{' '}
                {/* A file built before review #397 links claims by the month they were billed. */}
                {data && claimMonth(data)?.key === 'bm' ? 'resolved claims billed' : 'claims resolved'} in{' '}
                {monthSpan(months)}: the touches and touched claims the Touch profile counts
              </>
            ) : basis === 'claim' ? (
              <>
                {int(rows.length)} touches, by anyone, on claims billed to {ownerLabel} in{' '}
                {monthSpan(months)}
                {' · '}
                <span style={{ color: 'var(--ink-3)' }}>
                  close to the Touch profile, not exact until the next data build: this counts
                  every action of a touch, and claims still open
                </span>
              </>
            ) : (
              <>
                {int(rows.length)} touches of {int(data.n)} on file
              </>
            )}
            {hidden > 0 && (
              <>
                {' · '}
                <HiddenByStatus count={hidden} noun="touches" />
              </>
            )}
            {only.length > 0 && (
              <>
                {' · '}
                <span className="pill-sel">
                  {monthSpan(months)} only
                  <button type="button" title="Clear" onClick={() => setOnly([])}>
                    ×
                  </button>
                </span>
              </>
            )}
            {team && (
              <>
                {' · '}
                <span className="pill-sel">
                  {team.label}
                  <button type="button" title="Clear" onClick={() => setTeam(null)}>
                    ×
                  </button>
                </span>
              </>
            )}
          </>
        )
      }
      rows={rows}
      columns={columns}
      exportColumns={exportColumns}
      exportName="touch-detail.csv"
      rowKey={(i) => String(i)}
      empty={
        <p className="body-note" style={{ margin: 0 }}>
          {error ??
            (!data ? (
              'Loading touches…'
            ) : basis === 'touch' && owners !== null && (onClaims?.rows.length ?? 0) > 0 ? (
              <>
                {ownerLabel} made no touches in {monthSpan(months)}. The Touch profile counts the
                touches on the claims billed to them, by whoever made them.{' '}
                <button type="button" className="jump-link" onClick={() => setBasisWanted('claim')}>
                  Show the {int(onClaims!.rows.length)} touches on their claims
                </button>
              </>
            ) : basis === 'claim' && !onClaims ? (
              claimRows.error ?? 'Loading their claims…'
            ) : (
              'No touches match these filters.'
            ))}
        </p>
      }
    />
  )
}

const seg: React.CSSProperties = {
  display: 'inline-flex',
  border: '1px solid var(--rule-2)',
  borderRadius: 6,
  overflow: 'hidden',
  background: 'var(--surface)',
}

const segBtn: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 9px',
  border: 0,
  cursor: 'pointer',
}
